import { useCallback, useEffect, useMemo } from 'react';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { useUpdatePurchasesPermissions } from 'src/components/Purchases/BulkActions/useUpdatePurchasesPermissions';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { BulkEditPurchaseSettingsDialog } from 'src/dialogs/BulkEdits/BulkEditPurchaseSettingsDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditPreviewWithDetails,
  BulkEditPurchaseClient,
  CommandOptionType,
  PurchaseOrderDetailsInput,
  PurchaseOrderQuery,
} from 'src/WebApiController';

import { PURCHASE_BULK_UPDATE_GENERAL_SETTINGS_UPDATE_KEY } from '../../PurchaseActionDropdown.constants';
import { LaunchUpdatePurchaseSettings } from './LaunchUpdatePurchaseSettings';

export const useBulkUpdatePurchaseSettings = (
  filterQuery: PurchaseOrderQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  groupId?: string,
  shouldRegister?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasUpdatePurchasesPermission } = useUpdatePurchasesPermissions();

  const updateKey = groupId
    ? `purchaseSettings-event-${groupId}`
    : PURCHASE_BULK_UPDATE_GENERAL_SETTINGS_UPDATE_KEY;

  const { showErrorDialog } = useClientLogContext();
  const { refreshData } = usePurchaseDataContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const {
    openDialog: openBulkEditPurchaseSettingsDialog,
    closeDialog: closeBulkEditPurchaseSettingsDialog,
  } = useDialog(
    DialogId.BulkEditPurchaseSettings,
    BulkEditPurchaseSettingsDialog
  );

  const onSubmit = useCallback(
    async (
      purchaseInput: PurchaseOrderDetailsInput | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const client = new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            );

            const preview =
              await client.bulkUpdateGeneralSettingsPreview(filterQuery);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateGeneralSettingsPreview', error, {
              trackErrorData: { filterQuery },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (purchaseInput?.buyerUserId != null) {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            ).bulkUpdateGeneralSettings(
              {
                item1: preview!.preview,
                item2: purchaseInput.buyerUserId!,
              },
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                await refreshData(true);
                await refreshExpandedListItems();
              }

              closeBulkEditPurchaseSettingsDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateGeneralSettings', error, {
              trackErrorData: { preview, purchaseInput },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeBulkEditPurchaseSettingsDialog,
      filterQuery,
      refreshData,
      refreshExpandedListItems,
      setIsLoading,
      showErrorDialog,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkEditPurchaseSettingsDialog({
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditPurchaseSettingsDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.PurchasesBulkUpdatePurchaseSettings,
        onSelect: openDialogStableRef,
      },
      disabled: !hasUpdatePurchasesPermission || !shouldRegister,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.PurchasesBulkUpdatePurchaseSettings,
        disabled: !hasUpdatePurchasesPermission || !shouldRegister,
      });
    };
  }, [
    hasUpdatePurchasesPermission,
    shouldRegister,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkEditPurchaseSettingsDialog({
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditPurchaseSettingsDialog,
      });
    },
    [
      closeBulkEditPurchaseSettingsDialog,
      onSubmit,
      openBulkEditPurchaseSettingsDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasUpdatePurchasesPermission) {
      return [];
    }

    return [
      {
        title: ContentId.UpdatePurchasedBy,
        onClick: onClick,
        disabled: isLoading,
      },
    ];
  }, [hasUpdatePurchasesPermission, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchUpdatePurchaseSettings
        key="purchaseGeneralSettingsUpdate"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
